import type { ColumnDef } from '@tanstack/react-table';
import { Card, InputText, TableListData } from 'components';
import { Button, IUseFormStructureItem, InputWraper, useForm } from 'modules';
import React, { useCallback, useState } from 'react';
import {
  useGetCustomersListQuery,
  useExportCustomersListQuery,
} from 'services';
import type {
  CustomerInfoEntity,
  GetListCustomerInfoEntity,
} from 'services/openapi';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';
import { DEFAULT_PAGE } from 'configs';

import {
  columnsDefined,
  type FilterType,
  getInitFilterValues,
  exportHeaders,
} from './config';
import { useLocation } from 'react-router-dom';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';

const CustomersList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'customerCode',
      label: 'Mã khách hàng',
      defaultValue: initFilterValues?.customerCode,
    },
    {
      name: 'customerName',
      label: 'Tên khách hàng',
      defaultValue: initFilterValues?.customerName,
    },
    {
      name: 'customerPhoneNumber',
      label: 'Số điện thoại',
      defaultValue: initFilterValues?.customerPhoneNumber,
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } = useExportCustomersListQuery({
    ...{ variables },
    page: '1',
    pageSize: String(
      (dataTable as unknown as GetListCustomerInfoEntity)?.total,
    ),
  });

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...{ variables },
      page: DEFAULT_PAGE,
      pageSize: String(
        (dataTable as unknown as GetListCustomerInfoEntity)?.total,
      ),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];
    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });

    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.map((item: CustomerInfoEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        item.customerCode,
        item.customerName,
        normalizedPhoneNumber(item.customerPhoneNumber ?? ''),
        item.customerAddress,
        item.customerEmail,
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });
    // Save File
    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_khach_hang_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-4 col-12'>
              <InputWraper
                inputProps={getInputProps('customerCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-4 col-12'>
              <InputWraper
                inputProps={getInputProps('customerName')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-4 col-12'>
              <InputWraper
                inputProps={getInputProps('customerPhoneNumber')}
                renderInput={InputText}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>
      <TableListData<GetListCustomerInfoEntity>
        columns={columnsDefined as ColumnDef<GetListCustomerInfoEntity>[]}
        useQuery={useGetCustomersListQuery}
        filterKey='searchDetailContent'
        variables={variables}
        setVariables={setVariables}
        callBackData={setDataTable}
        orderColumn
      />
    </>
  );
};

export default CustomersList;
